import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShowSection } from 'framework/ui/layout';
import { FormFieldsData, UpdateForm, useFormContext } from 'framework/form';
import useBillingService, { BillingRequestSearchResultItem, BillingRequestStatus } from '../../billingService';
import { BillingType } from 'domain/shared/newProject';
import { SuccessMessage, useMessageService } from 'framework/ui/Message';
import { useBillingRouteContext } from '../../BillingRouteContext';
import BillingRequestFormPeriodicPrepaid from '../BillingRequestFormPeriodicPrepaid';
import BillingRequestFormPeriodicPostpaidOrMilestone from '../BillingRequestFormPeriodicPostpaidOrMilestone';
import { UpdateBillingRequestFormValues } from '../types';
import { prepareUpdateBillingFormData, createUpdateBillingPayload } from '../utils';

const BillingRequestFormDataSync = ({ formData }: { formData: UpdateBillingRequestFormValues | null }) => {
  const { setFormData } = useFormContext();

  useEffect(() => {
    if (formData) {
      setFormData?.(formData as FormFieldsData);
    }
  }, [formData, setFormData]);

  return null;
};

const UpdateBillingRequestLayout = () => {
  const { getBillingRequest, updateBillingRequest, getInitiateBillingValidationSchema } = useBillingService();
  const { billingScheduleId, billingRequestId } = useParams<Dictionary<string>>();
  const [refetchedFormData, setRefetchedFormData] = useState<UpdateBillingRequestFormValues | null>(null);
  const { addMessage } = useMessageService();
  const { billingType } = useBillingRouteContext();
  const handleGetData = async () => {
    const billingRequest = await getBillingRequest(billingScheduleId!, billingRequestId!);
    return prepareUpdateBillingFormData(billingRequest);
  };

  const handleSubmit = async (_requestId: StringOrNumber, values: FormFieldsData): Promise<object> => {
    const payload = createUpdateBillingPayload({
      ...(values as UpdateBillingRequestFormValues & { billingType: BillingType }),
    });
    await updateBillingRequest(billingScheduleId!, billingRequestId!, payload);
    return getBillingRequest(billingScheduleId!, billingRequestId!);
  };

  return (
    <UpdateForm
      idSource="billingRequestId"
      getDataFunc={handleGetData}
      submitFormFunc={handleSubmit}
      afterSubmit={{
        execute: (_id, response: object) => {
          const billingRequest = response as BillingRequestSearchResultItem;
          setRefetchedFormData(prepareUpdateBillingFormData(billingRequest));
          addMessage(
            billingRequest.status === BillingRequestStatus.DRAFT
              ? new SuccessMessage('You are awesome! Billing request has been successfully saved.')
              : new SuccessMessage('You are awesome! Billing request has been successfully sent for approval.'),
          );
        },
      }}
      getValidationSchemaFunc={getInitiateBillingValidationSchema}
    >
      <BillingRequestFormDataSync formData={refetchedFormData} />
      <ShowSection title="Update Billing Request:" titleVariant="h2">
        {billingType === BillingType.PERIODIC_PREPAID ? (
          <BillingRequestFormPeriodicPrepaid isUpdate />
        ) : (
          <BillingRequestFormPeriodicPostpaidOrMilestone isUpdate />
        )}
      </ShowSection>
    </UpdateForm>
  );
};

export default UpdateBillingRequestLayout;
